import React, { useState, useEffect, useRef } from 'react';
import { Bell, MessageSquare, Heart, UserPlus, X, Check, CheckCheck } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationBell = ({ playerId, onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    if (!playerId) return;
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [playerId]);

  const fetchUnreadCount = async () => {
    if (!playerId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/notifications/${playerId}/unread-count`);
      const data = await response.json();
      setUnreadCount(data.unread_notifications || 0);
      setUnreadMessages(data.unread_messages || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    if (!playerId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications/${playerId}?limit=20`);
      const data = await response.json();
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT'
      });
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/${playerId}/read-all`, {
        method: 'PUT'
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.notification_id);
    }
    
    // Navigate based on notification type
    if (notification.reference_type === 'message' && onNavigate) {
      onNavigate('messages');
    } else if (notification.reference_type === 'post' && onNavigate) {
      onNavigate('feed');
    }
    
    setIsOpen(false);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'message':
        return <MessageSquare className="w-4 h-4 text-blue-400" />;
      case 'comment':
        return <MessageSquare className="w-4 h-4 text-[#00d4aa]" />;
      case 'like':
        return <Heart className="w-4 h-4 text-red-400" />;
      case 'follow':
        return <UserPlus className="w-4 h-4 text-purple-400" />;
      default:
        return <Bell className="w-4 h-4 text-white/60" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  const totalUnread = unreadCount + unreadMessages;

  if (!playerId) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleBellClick}
        className="relative text-white/80 hover:text-[#00d4aa] hover:bg-white/5"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {totalUnread > 0 && (
          <span 
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1"
            data-testid="notification-badge"
          >
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-h-[400px] bg-[#131416] border border-white/10 rounded-xl shadow-xl overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <h3 className="text-white font-semibold">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs text-[#00d4aa] hover:text-[#33e0bb] hover:bg-white/5 h-7"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-3 h-3 mr-1" />
                  Mark all read
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="text-white/60 hover:text-white h-7 w-7"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Stats bar */}
          {(unreadCount > 0 || unreadMessages > 0) && (
            <div className="flex gap-4 p-2 bg-[#1a1b1e] text-xs">
              {unreadMessages > 0 && (
                <span className="text-blue-400">
                  <MessageSquare className="w-3 h-3 inline mr-1" />
                  {unreadMessages} new messages
                </span>
              )}
              {unreadCount > 0 && (
                <span className="text-[#00d4aa]">
                  <Bell className="w-3 h-3 inline mr-1" />
                  {unreadCount} notifications
                </span>
              )}
            </div>
          )}

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-[300px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-[#00d4aa] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 px-4">
                <Bell className="w-10 h-10 text-white/20 mx-auto mb-2" />
                <p className="text-white/40 text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {notifications.map((notification) => (
                  <div
                    key={notification.notification_id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-3 hover:bg-white/5 cursor-pointer transition-colors ${
                      !notification.read ? 'bg-[#00d4aa]/5' : ''
                    }`}
                    data-testid={`notification-${notification.notification_id}`}
                  >
                    <div className="flex gap-3">
                      {/* Icon or Avatar */}
                      <div className="flex-shrink-0">
                        {notification.sender_picture ? (
                          <img
                            src={notification.sender_picture}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#1a1b1e] flex items-center justify-center">
                            {getIcon(notification.type)}
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm">
                          {notification.sender_name && (
                            <span className="font-semibold">{notification.sender_name} </span>
                          )}
                          <span className="text-white/70">{notification.title}</span>
                        </p>
                        {notification.content && (
                          <p className="text-white/50 text-xs truncate mt-0.5">
                            {notification.content}
                          </p>
                        )}
                        <p className="text-white/40 text-xs mt-1">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>

                      {/* Unread indicator */}
                      {!notification.read && (
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-[#00d4aa] rounded-full" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t border-white/10 text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (onNavigate) onNavigate('messages');
                  setIsOpen(false);
                }}
                className="text-xs text-[#00d4aa] hover:text-[#33e0bb] w-full"
              >
                View all messages
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
